import { TuyauError } from '@tuyau/core/client'
import { AxiosError } from 'axios'
import { GENERIC_FORBIDDEN_MESSAGE, getApiErrorMessage } from '~/lib/utils'
import type { PermissionTuple } from '~/utils/ability'

/** Corps d'un 403 `RbacForbiddenException` du backend. */
interface RbacForbiddenBody {
  code: string
  /** « Forbidden », ou l'explication métier du refus quand le backend en donne une (#909). */
  message?: string
  missingPermissions: { action: string; resource: string }[]
}

/** Corps de la réponse 403, que l'appel vienne du client legacy (axios) ou de Tuyau (ky). */
const extractForbiddenBody = (error: unknown): Partial<RbacForbiddenBody> | null => {
  if (error instanceof AxiosError && error.response?.status === 403) return error.response.data
  if (error instanceof TuyauError && error.isStatus(403))
    return error.response as Partial<RbacForbiddenBody>
  return null
}

/** Tuples manquants si l'erreur est un refus RBAC (`E_RBAC_PERMISSION_DENIED`), sinon `null`. */
export const getRbacMissingPermissions = (error: unknown): PermissionTuple[] | null => {
  const body = extractForbiddenBody(error)
  if (body?.code !== 'E_RBAC_PERMISSION_DENIED' || !Array.isArray(body.missingPermissions)) {
    return null
  }

  // Les tuples viennent de la taxonomie côté backend ; `describePermission` retombe de toute façon
  // sur les libellés bruts pour un couple inconnu.
  return body.missingPermissions.map(
    ({ action, resource }) => [action, resource] as unknown as PermissionTuple,
  )
}

/** Toast d'erreur d'une mutation, décidé sans rendu : droits manquants ou simple message. */
export type MutationErrorToast =
  | { kind: 'rbac'; title: string; missingPermissions: PermissionTuple[] }
  | { kind: 'message'; message: string }

/**
 * Refus RBAC → droits manquants, titrés par l'explication métier du backend (#909) ou, à défaut,
 * par le message d'échec de l'action (`fallback`) ; autre erreur → message de la réponse ou
 * `fallback` (cf. `getApiErrorMessage`).
 */
export const resolveMutationErrorToast = (error: unknown, fallback: string): MutationErrorToast => {
  const missingPermissions = getRbacMissingPermissions(error)
  if (!missingPermissions) return { kind: 'message', message: getApiErrorMessage(error, fallback) }

  const explanation = extractForbiddenBody(error)?.message
  return {
    kind: 'rbac',
    title:
      typeof explanation === 'string' && explanation !== GENERIC_FORBIDDEN_MESSAGE
        ? explanation
        : fallback,
    missingPermissions,
  }
}
